/* eslint-disable max-lines */
import React, {
  ChangeEvent,
  FunctionComponent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  RouteComponentProps,
  Link as RouterLink,
  navigate,
  useLocation,
} from '@reach/router';
import { useEthers } from '@usedapp/core';
import NoLiquidity from '@views/Pools/PoolsWidget/RemoveLiquidity/NoLiquidity';

import AmountFiat from '@bitcoin-portal/verse-web-components/dist/AmountFiat';
import Body from '@bitcoin-portal/verse-web-components/dist/Body';
import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import Card from '@bitcoin-portal/verse-web-components/dist/Card/Card';
import Coin from '@bitcoin-portal/verse-web-components/dist/Coin';
import CoinPair from '@bitcoin-portal/verse-web-components/dist/CoinPair';
import H3 from '@bitcoin-portal/verse-web-components/dist/H3';
import EthAlertIcon from '@bitcoin-portal/verse-web-components/dist/Icons/EthAlert';
import InputNumber from '@bitcoin-portal/verse-web-components/dist/InputNumber';
import Label from '@bitcoin-portal/verse-web-components/dist/Label';
import Link from '@bitcoin-portal/verse-web-components/dist/Link';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { getSpotRate } from '@context/api/nekosdk';
import {
  DEFAULT_DECIMALS,
  EMPTY,
  EMPTY_ACCOUNT,
  PRECISION,
  VERSE_TOKEN,
} from '@context/constants';
import { useFarms } from '@context/providers/FarmsProvider';
import { useTrackedState } from '@context/store';

import { formatValue } from '@helpers/formatValue';
import {
  chainDetails,
  findProvider,
  getCustomDecimals,
  lpTokenValue,
  sanitizeInput,
  toFixed,
} from '@helpers/index';

import { useFormatBalance, useNativeBalance } from '@hooks/core';

import ScrollToTop from '../ScrollToTop';
import {
  AlertButton,
  Details,
  EthAlert,
  Header,
  SelectCurrencyContentWrapper,
  StyledChevron,
  ViewWrapper,
} from './styled';

interface FarmTransactionProps extends RouteComponentProps {
  title: string | ReactNode;
  triggerTransaction: (input: number, decimals: number) => void;
  buttonText: string;
  withdrawMode?: boolean;
  subtitle?: string;
  onConnectWallet?: () => void;
  view?: FarmsView;
  pendingView?: ReactNode;
  successView?: ReactNode;
  errorView?: ReactNode;
  reset?: () => void;
  page?: string;
  backLink?: string;
  customNoTokensProps?: {
    title?: string;
    message?: string;
    buttonText?: string;
    link?: (children: ReactNode) => ReactNode;
  };
  readOnly?: boolean;
}

export const FarmTransaction: FunctionComponent<FarmTransactionProps> = ({
  title,
  triggerTransaction,
  buttonText,
  withdrawMode,
  subtitle,
  onConnectWallet,
  view,
  pendingView,
  successView,
  errorView,
  reset,
  page = 'farms',
  customNoTokensProps = {},
  backLink,
  readOnly,
}) => {
  const { provider: exchangeProvider, selectedLiquidityPair } =
    useTrackedState();
  const { selectedFarm: farm, farmBalances } = useFarms();

  const provider = findProvider(exchangeProvider);
  const { chainId, account } = useEthers();
  const details = chainDetails(provider, chainId);
  const userAccount = account || EMPTY_ACCOUNT;

  const location = useLocation();
  const { newTx } = (location?.state as LocationState) || {
    newTx: false,
  };

  const [inputValue, setInputValue] = useState<string>(EMPTY);

  // wallet balance
  const [balance, setBalance] = useState<number>(0);
  const [balanceDecimals, setBalanceDecimals] = useState<number>(PRECISION);

  // staking token rate
  const [tokenRate, setTokenRate] = useState<number>(0);

  // --- PROPERTIES AND CALCULATIONS ---

  const tickerA = selectedLiquidityPair.token0?.symbol || '';
  const tickerB = selectedLiquidityPair.token1?.symbol || '';

  const { chainParam: chain = 'eth' } = findProvider(exchangeProvider);
  const pairUrlParam = `${tickerA.toLowerCase()}-${tickerB.toLowerCase()}`;

  const fiatInputValue0 = farm?.singleSided
    ? tokenRate * sanitizeInput(inputValue || '0')
    : lpTokenValue(sanitizeInput(inputValue || '0'), selectedLiquidityPair);

  // alerts
  const needTokens = !balance || balance < sanitizeInput(inputValue);
  const needEth = !useNativeBalance(userAccount);

  const stakedToken = farm?.singleSided
    ? farm.stakingToken?.token || VERSE_TOKEN.token
    : selectedLiquidityPair?.id;

  const balanceVerseXStaked = parseFloat(
    formatValue(farmBalances[farm.contractAddress], DEFAULT_DECIMALS),
  );

  const balanceVerseXFormatted = useFormatBalance(
    userAccount,
    stakedToken,
    DEFAULT_DECIMALS,
    details.chainId,
  );

  const precision = useMemo(() => {
    return 10 ** balanceDecimals;
  }, [balanceDecimals]);

  const roundedBalance = useMemo(() => {
    const bal = toFixed(
      Math.floor(balance * precision) / precision,
      balanceDecimals,
    );
    return bal;
  }, [balance, balanceDecimals]);

  // Reset tx if navigating to tx view for first time
  useEffect(() => {
    if (typeof reset === 'function' && newTx) {
      reset();
      navigate('.', {
        state: { newTx: false },
        replace: true,
      });
    }
  }, []);

  // Set Wallet Balances
  useEffect(() => {
    const walletBalance = parseFloat(
      withdrawMode ? balanceVerseXStaked.toString() : balanceVerseXFormatted,
    );
    setBalance(walletBalance);
    setBalanceDecimals(getCustomDecimals(walletBalance, PRECISION));
  }, [balanceVerseXStaked, balanceVerseXFormatted]);

  // Auto-fill input with max VerseX balance
  useEffect(() => {
    if (!withdrawMode) {
      const tokenMax = Math.floor(balance * precision) / precision;
      const formattedMax = tokenMax.toFixed(balanceDecimals);
      setInputValue(formattedMax);
    }
  }, [balance]);

  // Set Maximum Amount
  const maxInput = useCallback(() => {
    const tokenMax = Math.floor(balance * precision) / precision;
    const formattedMax = tokenMax.toFixed(balanceDecimals);
    setInputValue(formattedMax);
  }, [balance]);

  // Calculate staking token rate
  useEffect(() => {
    const fetchTokenRate = async () => {
      const res = await getSpotRate(farm?.stakingToken?.abbr || '');
      const rate = res.getValue();

      setTokenRate(rate?.USD?.rate || 0);
    };

    if (farm?.singleSided) fetchTokenRate();
    if (readOnly) {
      maxInput();
    }
  }, [farm]);

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  // Back button view
  const goBack = useCallback(() => {
    if (backLink) {
      navigate(backLink);
      return;
    }
    const farmView = withdrawMode ? 'details' : 'farmsList';
    const previousView = farm?.singleSided ? 'details' : farmView;

    const { fromRoot } = (location?.state as LocationState) || {
      fromRoot: false,
    };
    const { chainParam } = provider || {
      chainParam: 'eth',
    };
    const pairParam = farm?.singleSided
      ? farm?.customPath ?? farm?.stakingToken?.ticker
      : `${tickerA.toLowerCase()}-${tickerB.toLowerCase()}`;

    if (userAccount === EMPTY_ACCOUNT) {
      navigate(`/${page}/${chainParam}/`);
    } else if (previousView === 'details' || !fromRoot) {
      navigate(`/${page}/${chainParam}/${pairParam}/`);
    } else {
      navigate(`/${page}/${chainParam}/`);
    }
  }, [withdrawMode, provider, selectedLiquidityPair.id]);

  const noTokensView = useMemo(() => {
    const signedIn = userAccount !== EMPTY_ACCOUNT;

    if (!signedIn) return 'notConnected';
    if (!balance && signedIn) return 'noTokens';

    return '';
  }, [userAccount, balance]);

  const noTokensProps = {
    title: withdrawMode ? 'global.withdraw.nothing' : 'global.deposit.nothing',
    message: withdrawMode
      ? 'farms.withdraw.noTokens'
      : 'farms.deposit.noTokens',
    buttonText: 'farms.routeFarmsButton',
    link: (children: ReactNode) => {
      return (
        <RouterLink to={`/farms/${chain}/${pairUrlParam}/`}>
          {children}
        </RouterLink>
      );
    },
    ...customNoTokensProps,
  };

  const notConnectedProps = {
    title: 'global.labels.notConnected',
    message: withdrawMode ? 'farms.connect' : 'farms.deposit.connect',
    buttonText: 'global.labels.connectWallet',
  };

  return (
    <>
      {view === 'pending' && pendingView}
      {view === 'success' && successView}
      {view === 'error' && errorView}
      {view === '' && (
        <ViewWrapper noHorizontalPadding={!!noTokensView}>
          <ScrollToTop />
          <Header>
            <StyledChevron onClick={goBack} />
            {farm?.singleSided ? (
              <Coin
                alt={farm.pair.token0?.symbol}
                isErc
                ticker={farm.pair.token0?.symbol}
                size="44"
              />
            ) : (
              <CoinPair
                coinA={{
                  ticker: tickerA,
                  altSrc: selectedLiquidityPair.token0?.icon,
                  size: '44px',
                }}
                coinB={{
                  ticker: tickerB,
                  altSrc: selectedLiquidityPair.token1?.icon,
                  size: '44px',
                }}
              />
            )}
            <H3 style={{ padding: '16px 0 8px' }}>{title}</H3>
            {subtitle && (
              <Body style={{ color: colors.shade80 }}>{subtitle}</Body>
            )}
          </Header>

          {noTokensView === 'notConnected' && (
            <NoLiquidity
              userAccount={account}
              onConnectWallet={onConnectWallet}
              {...notConnectedProps}
            />
          )}
          {noTokensView === 'noTokens' && (
            <NoLiquidity
              userAccount={account}
              onConnectWallet={onConnectWallet}
              singleSided={farm?.singleSided}
              {...noTokensProps}
            />
          )}
          {!noTokensView && (
            <>
              <InputNumber
                readOnly={readOnly}
                allowNegative={false}
                thousandSeparator
                action={
                  <div
                    style={{
                      display: 'flex',
                      flexFlow: 'row nowrap',
                      paddingRight: 16,
                    }}
                  >
                    {!readOnly && (
                      <Button
                        design="clear"
                        style={{ padding: '0 16px' }}
                        onClick={maxInput}
                      >
                        MAX
                      </Button>
                    )}
                    <SelectCurrencyContentWrapper selected>
                      <div>
                        <Coin alt="Verse-X" isErc ticker="VERSE-X" size="20" />
                      </div>
                      <Body style={{ fontWeight: 600 }}>
                        {farm?.singleSided ? (
                          <>{farm.stakingToken?.abbr}</>
                        ) : (
                          <>
                            VERSE-X ({tickerA}/{tickerB})
                          </>
                        )}
                      </Body>
                    </SelectCurrencyContentWrapper>
                  </div>
                }
                details={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>
                      Available: {roundedBalance}{' '}
                      {farm?.singleSided ? (
                        <>{farm.stakingToken?.abbr}</>
                      ) : (
                        <>
                          VERSE-X ({tickerA}/{tickerB})
                        </>
                      )}
                    </span>
                    <span>
                      ~
                      <AmountFiat
                        amount={fiatInputValue0}
                        currency="usd"
                        locale="en-US"
                      />
                    </span>
                  </div>
                }
                value={inputValue}
                onChange={handleValueChange}
                error={
                  needTokens ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      Available: {roundedBalance} VERSE-X
                      <Body size="medium">Insufficient tokens</Body>
                    </div>
                  ) : undefined
                }
              />

              {!withdrawMode && (
                <Details>
                  <Card>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: colors.shade80,
                      }}
                    >
                      <Label
                        style={{
                          color: colors.shade80,
                        }}
                      >
                        {farm?.singleSided ? 'Staking' : 'Farm'} APY:
                      </Label>
                      <Label style={{ color: colors.white }}>
                        {farm?.apy}%
                      </Label>
                    </div>
                  </Card>
                </Details>
              )}

              {needEth && (
                <EthAlert design="error" icon={<EthAlertIcon size={40} />}>
                  <div style={{ gridArea: 'text' }}>
                    <Label>Not enough ETH</Label>
                    <Label
                      small
                      style={{
                        color: colors.shade90,
                        paddingTop: 4,
                      }}
                    >
                      To deposit into this Farm, you need ETH to cover network
                      fees
                    </Label>
                  </div>
                  <AlertButton small>
                    <Link href="https://buy.bitcoin.com/eth/" newTab>
                      Get ETH
                    </Link>
                  </AlertButton>
                </EthAlert>
              )}

              <Button
                fullWidth
                secondary
                disabled={
                  (!farm?.singleSided &&
                    !selectedLiquidityPair.token0?.symbol) ||
                  (!farm?.singleSided &&
                    !selectedLiquidityPair.token1?.symbol) ||
                  needTokens ||
                  !fiatInputValue0
                }
                onClick={(e: MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  triggerTransaction(
                    sanitizeInput(inputValue || '0'),
                    balanceDecimals,
                  );
                }}
                style={{
                  marginTop: 29,
                }}
              >
                {buttonText}
              </Button>
            </>
          )}
        </ViewWrapper>
      )}
    </>
  );
};

export default FarmTransaction;
